import { computed, ref, watch } from 'vue'

export type PreviewDevicePlatform = 'ios' | 'android' | 'mini-program' | 'h5' | 'tablet'

export interface PreviewDevicePreset {
  code: string
  name: string
  width: number
  height: number
  platform: PreviewDevicePlatform
}

export const PREVIEW_DEVICE_PRESETS: PreviewDevicePreset[] = [
  { code: 'iphone-14', name: 'iPhone 14 (390×844)', width: 390, height: 844, platform: 'ios' },
  { code: 'iphone-15-pro', name: 'iPhone 15 Pro (393×852)', width: 393, height: 852, platform: 'ios' },
  { code: 'wechat-mini-program', name: '微信小程序 (标准 375×667)', width: 375, height: 667, platform: 'mini-program' },
  { code: 'wechat-miniapp-ip14', name: '微信小程序 (iPhone 14 390×844)', width: 390, height: 844, platform: 'mini-program' },
  { code: 'pixel-8', name: 'Pixel 8 (360×780)', width: 360, height: 780, platform: 'android' },
  { code: 'uniapp-h5', name: 'UniApp / H5 (375×812)', width: 375, height: 812, platform: 'h5' },
  { code: 'ipad-air', name: 'iPad 平板 (820×1180)', width: 820, height: 1180, platform: 'tablet' }
]

const STORAGE_KEY = 'ren-decoration-preview-device'
const LEGACY_DEVICE_CODES: Record<string, string> = {
  'wechat-miniapp': 'wechat-mini-program'
}

export const PREVIEW_DESIGN_WIDTH = 375

export function previewBezelSize(platform: PreviewDevicePlatform): number {
  return platform === 'tablet' ? 14 : 10
}

export function previewDesignScale(deviceWidth: number, designWidth = PREVIEW_DESIGN_WIDTH): number {
  if (deviceWidth <= 0 || designWidth <= 0) return 1
  return deviceWidth / designWidth
}

export function previewFitScale(
  availableWidth: number,
  availableHeight: number,
  stageWidth: number,
  stageHeight: number,
  bezel: number,
): number {
  const outerWidth = stageWidth + bezel * 2
  const outerHeight = stageHeight + bezel * 2
  if (availableWidth <= 0 || availableHeight <= 0 || outerWidth <= 0 || outerHeight <= 0) return 1
  return Math.max(0.5, Math.min(availableWidth / outerWidth, availableHeight / outerHeight, 1))
}

export function resolvePreviewDeviceCode(code?: string | null): string {
  const normalized = LEGACY_DEVICE_CODES[code ?? ''] ?? code
  if (normalized && PREVIEW_DEVICE_PRESETS.some((device) => device.code === normalized)) {
    return normalized
  }
  return PREVIEW_DEVICE_PRESETS[0].code
}

function readStoredDeviceCode(): string | undefined {
  if (typeof window === 'undefined') return undefined
  return window.sessionStorage.getItem(STORAGE_KEY) ?? undefined
}

export function useDecorationDevice(initialCode?: string) {
  const previewDeviceCode = ref(resolvePreviewDeviceCode(initialCode ?? readStoredDeviceCode()))
  const previewScale = ref(1)

  const previewDevice = computed<PreviewDevicePreset>(() => {
    return (
      PREVIEW_DEVICE_PRESETS.find((d) => d.code === previewDeviceCode.value) ??
      PREVIEW_DEVICE_PRESETS[0]
    )
  })

  const previewDeviceStyle = computed(() => {
    const contentHeight = previewDevice.value.height - 42
    return {
      '--ren-preview-width': `${previewDevice.value.width}px`,
      '--ren-preview-height': `${previewDevice.value.height}px`,
      '--ren-preview-content-height': `${contentHeight}px`,
      '--ren-preview-content-ratio': `${previewDevice.value.width} / ${contentHeight}`,
      '--ren-preview-bezel': `${previewBezelSize(previewDevice.value.platform)}px`,
      '--ren-preview-scale': String(previewScale.value),
      '--ren-design-width': `${PREVIEW_DESIGN_WIDTH}px`,
      '--ren-design-scale': String(previewDesignScale(previewDevice.value.width)),
    }
  })

  function setDevice(code: string) {
    previewDeviceCode.value = resolvePreviewDeviceCode(code)
  }

  function setScale(scale: number) {
    previewScale.value = Math.max(0.5, Math.min(2, scale))
  }

  watch(previewDeviceCode, (value) => {
    if (typeof window === 'undefined') return
    window.sessionStorage.setItem(STORAGE_KEY, value)
  })

  return {
    previewDeviceCode,
    previewDevicePresets: PREVIEW_DEVICE_PRESETS,
    previewDevice,
    previewDeviceStyle,
    previewScale,
    setDevice,
    setScale
  }
}
